import { Box, Center, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SourcingEventStatus, SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { NotReadyCard } from "../NotReadyCard";
import { isEvaluationLocked } from "../qualification/stage";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { AwardOutcome } from "./AwardOutcome";
import { canSelectWinner } from "./canSelectWinner";
import { ProductAwardSummary } from "./ProductAwardSummary";
import { SelectWinner } from "./SelectWinner";

export const AwardTab = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const { rounds } = useRoundSelection();
  const [searchParams] = useSearchParams();
  const evaluationIsDone = canSelectWinner(event.status, rounds);
  const toQualification = new URLSearchParams(searchParams);
  toQualification.set("tab", "qualification");

  switch (event.status) {
    // Picking a winner is the conclusion of the evaluation, not a shortcut past it: the scores and
    // the product basket are still moving while the event is being evaluated.
    case SourcingEventStatus.Evaluation:
      return (
        <Box>
          {evaluationIsDone ? (
            <SelectWinner event={event} />
          ) : (
            <NotReadyCard
              title={String(t("The award is not ready yet"))}
              description={String(
                t(
                  "Complete the evaluation before you select a winner. You do that with the Complete evaluation toggle on the evaluation tab.",
                ),
              )}
            />
          )}
          <ProductAwardSummary event={event} />
        </Box>
      );
    // A completed evaluation is locked but not yet awarded — the buyer awards from here.
    // If the event was already awarded before being completed, show the read-only outcome.
    case SourcingEventStatus.Completed:
      return (
        <Box>
          {event.award ? <AwardOutcome event={event} readOnly /> : <SelectWinner event={event} />}
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.AwardDecision:
    case SourcingEventStatus.Awarded:
      return (
        <Box>
          <AwardOutcome event={event} />
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.Closed:
      return (
        <Box>
          <AwardOutcome event={event} readOnly />
          <ProductAwardSummary event={event} />
        </Box>
      );
    case SourcingEventStatus.Cancelled:
      return (
        <Center py="16">
          <Text color="smMuted">{t("This procurement has been cancelled")}</Text>
        </Center>
      );
    default:
      return isEvaluationLocked(event) ? (
        <NotReadyCard
          title={String(t("The award is not ready yet"))}
          description={String(
            t("The qualification must be completed before the bids can be evaluated and awarded."),
          )}
          action={{
            label: String(t("Go to the qualification review")),
            to: `?${toQualification}`,
          }}
        />
      ) : (
        <NotReadyCard
          title={String(t("The award is not ready yet"))}
          description={String(
            t("Awarding becomes available once bidding is closed and bids are under evaluation."),
          )}
        />
      );
  }
};
